import { BasicOverlay, typographyRem } from '@by-miles/bymiles-styles'
import dayjs from 'dayjs'
import React, { useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { ContactUsWrapper } from '../components/ContactUsWrapper'
import { CoverBenefitsWrapper } from '../components/CoverBenefitsWrapper'
import { CoverDetails } from '../components/CoverDetails/CoverDetails'
import { CoverHighLights } from '../components/CoverHighlights'
import { FAQAccordion } from '../components/coverPage/FAQAccordion'
import { ExcessDropdown } from '../components/ExcessDropdown'
import { Extras } from '../components/Extras'
import { InceptionDateDropdown } from '../components/InceptionDateDropdown'
import {
  BreakdownCoverOverlay,
  CarCoverOverlay,
  ExcessOverlay,
  ExtrasPopup,
  GenericOverlay,
  IdScoreOverlay,
  LegalCoverOverlay,
  PolicyUsageOverlay,
} from '../components/Overlays'
import { SectionTitle } from '../components/SectionTitle'
import config from '../config'
import { BasePage, BasePageProps } from '../HOCs/BasePage'
import { ProviderContext } from '../Provider'
import { handleQuoteProgress } from '../utils/handleQuoteProgress'
import { onlyImmobiliserIsIdScore } from '../utils/onlyImmobiliserIsIdScore'
import { CoverPageStyles } from './styles/CoverPageStyles'
import { PagesGenericStyling } from './styles/PagesGenericStyling'
const { hero: Hero, paragraphBody: ParagraphBody } = typographyRem

const CurrentCover = ({ quote }: BasePageProps) => {
  const history = useHistory()
  const { state, handleShowModal, simpleStateUpdater, handleClickedExtras, handleCloseModal } =
    useContext(ProviderContext)

  useEffect(() => {
    const target = window.location.pathname
    const status = quote?.status?.sections
    const progress = handleQuoteProgress(status, target)
    if (target !== progress) {
      history.push(progress)
    }
  }, [])

  const interactedWithExtras = state.extras.interactedWithExtras
  const breakdownCoverOptions = quote?.breakdownCoverOptions || []
  const legalCoverOptions = quote?.legalCoverOptions || []
  const carHireCoverOptions = quote?.carHireCoverOptions || []

  //TODO: move that to the API

  const hasBreakdown = breakdownCoverOptions.some((tier: any) => tier?.selected)
  const hasLegal = legalCoverOptions?.some((tiers: any) => tiers?.selected)
  const hasCarHire = carHireCoverOptions?.some((tiers: any) => tiers?.selected)

  const hasExtrasSelected = hasBreakdown || hasLegal || hasCarHire
  const hasSelectedAllExtras = hasBreakdown && hasLegal && hasCarHire

  const location = useLocation()

  useEffect(() => {
    if (!quote?.pricing) return
    if (onlyImmobiliserIsIdScore(quote?.pricing?.immobiliser) && !quote?.isAllowListed) {
      handleShowModal('zero-id-score')
    }
    if (!hasSelectedAllExtras && !interactedWithExtras && !state.extrasPopupSeen) {
      simpleStateUpdater([{ state: 'shouldShowExtrasPopUp', value: true }])
    }
  }, [quote, location])

  return (
    <CoverPageStyles>
      <PagesGenericStyling>
        <Hero className="hero-heading LoNotSensitive">Your cover.</Hero>
        <CoverHighLights insurer={quote?.pricing?.insurerName || ''} coverage={quote?.pricing?.usage || ''} />
        <SectionTitle title="Included as standard." />
        <div className="unnecessary-wrapper">
          <CoverBenefitsWrapper
            hasChildren={quote?.details?.proposer?.children || 0}
            ncd={quote?.details?.proposer?.ncd || 0}
          />
        </div>
        <>
          <SectionTitle title={hasExtrasSelected ? 'Your extra cover.' : 'Want to add extra cover?'} />
          <div className="unnecessary-wrapper LoNotSensitive" id="sticky-entry-point">
            <Extras
              breakdownCoverTiers={breakdownCoverOptions}
              legalCoverTiers={legalCoverOptions}
              carHireCoverTiers={carHireCoverOptions}
              handleOnClick={(type: string) => handleClickedExtras(type, false)}
              hasBreakdown={hasBreakdown}
              hasLegal={hasLegal}
              hasCarHire={hasCarHire}
              tabFocusTrap={state.showModal ? -1 : 0}
            />
          </div>
        </>
        <SectionTitle title="Your details." />
        <CoverDetails
          additionalDrivers={quote?.details?.additional_drivers || []}
          mainDriver={{
            firstNames: quote?.details?.proposer?.first_names,
            lastNames: quote?.details?.proposer?.last_names,
          }}
          vehicle={{
            reg: quote?.details?.vehicle?.reg,
            make: quote?.details?.vehicle?.make,
            model: quote?.details?.vehicle?.model,
            trim: quote?.details?.vehicle?.trim,
          }}
          onClick={() => (window.location.href = `${config.checkoutOrigin}/details/car`)}
          tabFocusTrap={state.showModal ? -1 : 0}
        />
        {quote && (
          <div className="excess-wrapper" id="breakdown-price">
            <SectionTitle title="Your policy dates." />
            <InceptionDateDropdown
              inceptionDate={quote?.details.policy?.inception_date || dayjs().toISOString()}
              onInfoClick={() => handleShowModal('policy-start')}
            />
          </div>
        )}
        {quote && (
          <div className="excess-wrapper">
            <SectionTitle title="Your excess." />
            <ExcessDropdown
              voluntaryExcess={quote?.details.policy?.excess || 0}
              compulsoryExcess={quote?.pricing?.compulsoryExcess || 0}
              isMobile={state.isMobile}
              onInfoClick={() => handleShowModal('excess')}
            />
          </div>
        )}
        <div className="component-spacing">
          <SectionTitle title="Frequently asked questions." />
          <FAQAccordion />
        </div>

        <div className="get-in-touch-wrapper">
          <SectionTitle title="Get in touch." />
          <ContactUsWrapper />
        </div>
      </PagesGenericStyling>

      {state.showModal && state.activeModal === 'policy-start' && (
        <GenericOverlay
          onClose={() => handleCloseModal('policy-start')}
          overlay={
            <BasicOverlay
              heading="More about the start date."
              content={
                <ParagraphBody>
                  Your start date must be within the next 30 days. Your Insurance cover will start immediately from this
                  date. If you choose today as your start date, your cover will start from the second you purchase the
                  policy, and this exact time will be shown on your policy documents.
                </ParagraphBody>
              }
              moreRoom={true}
            />
          }
        />
      )}

      {state.showModal && state.activeModal === 'excess' && (
        <GenericOverlay
          overlay={
            <ExcessOverlay
              voluntaryExcess={quote?.details.policy?.excess || 0}
              compulsoryExcess={quote?.pricing?.compulsoryExcess || 0}
              isTrackerless={state.isTrackerless}
            />
          }
        />
      )}

      {state.showModal && state.activeModal === 'extras-popup' && state.shouldShowExtrasPopUp && (
        <ExtrasPopup handleContinue={() => history.push('/how', { from: '/cover' })} />
      )}

      {state.showModal && state.activeModal === 'breakdown-cover' && <BreakdownCoverOverlay />}
      {state.showModal && state.activeModal === 'legal-cover' && <LegalCoverOverlay />}
      {state.showModal && state.activeModal === 'car-cover' && <CarCoverOverlay />}
      {state.showModal && state.activeModal === 'policy-usage' && (
        <PolicyUsageOverlay usageCode={quote?.pricing?.usageCode || ''} coverage={quote?.pricing?.usage || ''} />
      )}
      {state.showModal && state.activeModal === 'zero-id-score' && (
        <GenericOverlay
          hideDismissIcon
          onClose={() => {}}
          overlay={
            <IdScoreOverlay
              firstName={quote?.details.proposer?.first_names!}
              lastName={quote?.details.proposer?.last_names!}
              dob={dayjs(quote?.details.proposer?.dob!).format('DD MMM YYYY')}
              addressLineOne={quote?.details?.policy?.address?.line_1 || ''}
              addressLineTwo={quote?.details?.policy?.address?.line_2 || ''}
              addressCity={quote?.details?.policy?.address?.city!}
              addressPostcode={quote?.details?.policy?.address?.postcode!}
              onClickContinue={() => (window.location.href = `${config.checkoutOrigin}/sorry/details`)}
              onClickChange={() => (window.location.href = `${config.checkoutOrigin}/details/car`)}
            />
          }
        />
      )}
    </CoverPageStyles>
  )
}

export default BasePage(CurrentCover)
